"use client"
import { useTranslations } from "next-intl"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import { Info, Pencil, Trash2 } from "lucide-react"
import { MobileFriendlyTooltip } from "@/components/ui/mobile-friendly-tooltip"
import { useMobileInputScroll } from "@/hooks/use-mobile-input-scroll"
import { creationFlowBlockClass, creationFlowInputClass } from "@/lib/creation-flow-form-styles"
import { formatPrice } from "@/lib/utils"
import { cn } from "@/lib/utils"
import type React from "react"
import { getCurrencySymbol, isZeroDecimalCurrency } from "@/lib/currencies"

type ShippingOptionProps = {
  option: {
    id: string
    name: string
    description: string
    icon: React.ElementType
    price: string
    bannedCountries: string[]
    allowCombined?: boolean
    combinedFee?: string
  }
  currencyCode?: string
  isSelected: boolean
  onToggle: (id: string) => void
  onToggleCombined: (id: string) => void
  onUpdateCombinedFee: (id: string, fee: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  allowContainerToggle?: boolean
  variant?: "default" | "dark"
  standalone?: boolean
}

export function ShippingOptionItem({
  option,
  currencyCode = "USD",
  isSelected,
  onToggle,
  onToggleCombined,
  onUpdateCombinedFee,
  onEdit,
  onDelete,
  allowContainerToggle = false,
  variant = "default",
  standalone = false,
}: ShippingOptionProps) {
  const t = useTranslations("shippingSettings");
  const tLC = useTranslations("listingCreation");
  const Icon = option.icon
  const combinedFeeInputRef = useMobileInputScroll<HTMLInputElement>()
  const currencySymbol = getCurrencySymbol(currencyCode)
  const zeroDecimalCurrency = isZeroDecimalCurrency(currencyCode)
  const dark = variant === "dark"
  const containerProps = allowContainerToggle && !standalone
    ? { onClick: () => onToggle(option.id), className: "cursor-pointer" }
    : {}
  const cardClass = dark
    ? cn(creationFlowBlockClass, "rounded-3xl p-4")
    : standalone
      ? "rounded-2xl bg-muted/30 p-4"
      : isSelected
        ? "rounded-2xl bg-primary/5 p-4"
        : "rounded-2xl bg-muted/30 p-4 hover:bg-muted/40"
  // The "dark" variant's colour literals became tokens in Phase 2 step 2d:
  // creationFlowBlockClass (this variant's card) is theme-driven now, so a
  // hardcoded text-white here would be invisible on a light page (the
  // shipping-settings mobile dialect). Tokens are byte-equal inside the
  // dark-scoped overlays (shipping-step, EditShippingTab).
  const iconClass = "text-muted-foreground"
  const labelClass = "text-sm font-medium cursor-pointer"
  const descClass = dark ? "text-xs mt-0.5 text-muted-foreground/70" : "text-xs mt-0.5 text-muted-foreground"
  const inputClass = dark ? "max-w-[120px] rounded-xl " + creationFlowInputClass : "max-w-[120px] rounded-xl"

  const switchTrackClass = dark
    ? "data-[state=unchecked]:bg-foreground/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary/20"
    : ""

  return (
    <div
      className={cn("flex flex-col transition-all duration-200", cardClass)}
      {...containerProps}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {!standalone && (
            <Checkbox
              id={option.id}
              checked={isSelected}
              onCheckedChange={() => onToggle(option.id)}
              className="shrink-0"
            />
          )}
          <Icon className={cn("h-5 w-5 shrink-0", iconClass)} />
          <div className="min-w-0 flex-1">
            <Label htmlFor={option.id} className={cn("cursor-pointer truncate block", labelClass)}>
              {option.name}
            </Label>
            <p className={cn("truncate", descClass)}>
              {option.description} · {formatPrice(option.price, currencyCode)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0 ml-2">
          <MobileFriendlyTooltip content={<p>{t("editTooltip")}</p>} shift="left">
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-9 w-9", dark && "text-muted-foreground hover:text-foreground hover:bg-sheet-handle")}
              onClick={(e) => {
                e.stopPropagation()
                onEdit(option.id)
              }}
            >
              <Pencil className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
          </MobileFriendlyTooltip>
          <MobileFriendlyTooltip content={<p>{t("deleteTooltip")}</p>} shift="left">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation()
                onDelete(option.id)
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          </MobileFriendlyTooltip>
        </div>
      </div>

      {!standalone && isSelected && (
        <div className="mt-4 pt-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Info className={cn("h-5 w-5 shrink-0 cursor-help", iconClass)} />
              <div className="min-w-0">
                <Label htmlFor={`combined-${option.id}`} className="text-sm font-medium cursor-pointer block">
                  {tLC("allowCombinedShipping")}
                </Label>
                <p className={cn("text-xs mt-0.5", descClass)}>
                  {tLC("combinedShippingDesc")}
                </p>
              </div>
            </div>
            <Switch
              id={`combined-${option.id}`}
              checked={option.allowCombined}
              onCheckedChange={() => onToggleCombined(option.id)}
              className={cn("shrink-0", switchTrackClass)}
            />
          </div>
          {option.allowCombined && (
            <div className="mt-3 flex items-center gap-2">
              <Label htmlFor={`fee-${option.id}`} className={cn("text-xs shrink-0", descClass)}>
                {tLC("extraFee")}
              </Label>
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-sm shrink-0">{currencySymbol}</span>
                <Input
                  ref={combinedFeeInputRef}
                  id={`fee-${option.id}`}
                  type="text"
                  inputMode={zeroDecimalCurrency ? "numeric" : "decimal"}
                  pattern={zeroDecimalCurrency ? "[0-9]*" : "[0-9]*\\.?[0-9]*"}
                  placeholder="0"
                  value={option.combinedFee === "0" ? "" : option.combinedFee}
                  onChange={(e) => {
                    const value = e.target.value
                    const regex = zeroDecimalCurrency ? /^[0-9]*$/ : /^[0-9]*\.?[0-9]*$/
                    if (value === "" || regex.test(value)) {
                      onUpdateCombinedFee(option.id, value)
                    }
                  }}
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
